import React from 'react';
import { FB, FBAutocompleteAsyncOption, FBAutocompleteAsyncState } from '..';
import FBAutocompleteAsyncStore from '../FBAutocompleteAsync/FBAutocompleteAsync.store';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import { SerialNumber } from '../FBSerials/types';

export const useSerialNumbersOptions = (id: string) => {
  const baseOptionId = FBAutocompleteAsyncOption.LHRSerial;
  // Create a unique key by combining optionId with id to avoid conflicts
  const uniqueOptionId = id ? `${baseOptionId}_${id}` : baseOptionId;
  const autocompleteAsyncState = FB.useRef<FBAutocompleteAsyncState>(FBAutocompleteAsyncState, { optionId: baseOptionId });

  React.useEffect(() => {
    if (!id) {
      return;
    }

    const existingData = FBAutocompleteAsyncStore.data.get(uniqueOptionId);
    if (existingData && existingData.size > 0) {
      return;
    }

    autocompleteAsyncState.setOptionId(baseOptionId);
    autocompleteAsyncState.fetchAutocompleteOption(id, baseOptionId);
  }, [autocompleteAsyncState, id, baseOptionId, uniqueOptionId]);

  // Copy the fetched rows into the shared store here, not while rendering: writing to an observable
  // inside the observer that reads it would re-trigger it on every render.
  React.useEffect(() => reaction(
    () => autocompleteAsyncState.autocompleteValueApi.data,
    (apiData) => {
      if (!id || !apiData) { return; }
      const existing = FBAutocompleteAsyncStore.data.get(uniqueOptionId);
      if (existing && existing.size > 0) { return; }
      FBAutocompleteAsyncStore.setOptionData(uniqueOptionId, Array.isArray(apiData) ? apiData : [apiData]);
    },
    { fireImmediately: true },
  ), [autocompleteAsyncState, id, uniqueOptionId]);

  return useObserver(() => {
    if (!id) {
      return { serialNumbersOptions: [] };
    }
    const asyncData = FBAutocompleteAsyncStore.data.get(uniqueOptionId) as Map<string, SerialNumber> | undefined;
    return { serialNumbersOptions: Array.from(asyncData?.values() ?? []) };
  });
};

// LHR rows from the Affected LHR(s) list carry hasSerialNumbers (BE autocomplete).
// Unknown (list not loaded yet) counts as having serials, so rows never vanish by mistake.
export const lhrHasNoSerialNumbers = (lhrId: string): boolean => {
  const lhrs = FBAutocompleteAsyncStore.data.get(FBAutocompleteAsyncOption.LHR) as Map<string, { id: string, hasSerialNumbers?: boolean }> | undefined;
  const lhr = Array.from(lhrs?.values() ?? []).find((item) => item.id === lhrId);
  return lhr?.hasSerialNumbers === false;
};
